import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Empty,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import { ImportOutlined, InboxOutlined, UserAddOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import type { CreateStudentRequest } from '@velora/contracts';
import { StepFooter } from '../StepFooter';
import { ApiError, productApi } from '../../../api';

/**
 * Setup step 1 — Students & guardians.
 * Two paths:
 *   1. Add one at a time in the Students directory (link at top)
 *   2. Bulk upload an XLSX matching our column spec (card below)
 *
 * The Excel path is deliberately strict: we require the exact columns rather
 * than guess. AI column-mapping arrives in a later iteration.
 *
 * Required columns (case-insensitive, spaces or underscores tolerated):
 *   admission_number, name, gender, dob, grade, section,
 *   guardian_name, guardian_phone, guardian_relation
 */

const REQUIRED_COLS = [
  'admission_number', 'name', 'gender', 'dob', 'grade', 'section',
  'guardian_name', 'guardian_phone', 'guardian_relation',
] as const;
type RequiredCol = (typeof REQUIRED_COLS)[number];

export function ImportPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['students', institution, {}],
    queryFn: () => productApi.listStudents(institution),
    enabled: !!institution,
  });
  const total = list.data?.total ?? 0;
  const canMarkDone = total > 0;

  const [parseError, setParseError] = useState<string | null>(null);
  const [rows, setRows] = useState<CreateStudentRequest[] | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [result, setResult] = useState<null | {
    created: number;
    submitted: number;
    errors: Array<{ row_index: number; admission_number: string; reason: string; detail?: string }>;
  }>(null);

  const importMut = useMutation({
    mutationFn: () => productApi.bulkImportStudents(institution, { rows: rows! }),
    onSuccess: (r) => {
      setResult(r);
      setRows(null);
      setFileName('');
      void qc.invalidateQueries({ queryKey: ['students', institution] });
      void message.success(`Imported ${r.created} of ${r.submitted}`);
    },
    onError: (e) => {
      void message.error((e instanceof ApiError && (e.body as { message?: string })?.message) || String(e));
    },
  });

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ marginBottom: 14 }}>
        <Typography.Title level={2} style={{ margin: 0 }}>Students and guardians</Typography.Title>
        <Typography.Text type="secondary">
          The whole product hangs off this list. Add students one at a time in the directory, or
          upload a spreadsheet below to bulk-load a class.
        </Typography.Text>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginBottom: 14 }}>
        <Card size="small"><Statistic title="On file" value={total} /></Card>
        <Card size="small"><Statistic title="Enrolled" value={list.data?.rows.filter((r) => r.status === 'enrolled').length ?? 0} /></Card>
        <Card size="small"><Statistic title="With primary guardian" value={list.data?.rows.filter((r) => r.primary_guardian).length ?? 0} /></Card>
      </div>

      <Space size={8} style={{ marginBottom: 14 }}>
        <Button type="primary" icon={<UserAddOutlined />} href={`/i/${institution}/admin/students`}>
          Add students in directory
        </Button>
      </Space>

      <Card size="small" title="Bulk import from Excel" style={{ marginBottom: 14 }}>
        <Typography.Paragraph type="secondary" style={{ fontSize: 12.5, marginBottom: 10 }}>
          Required columns (case-insensitive; spaces or underscores fine):{' '}
          <code style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11.5 }}>
            {REQUIRED_COLS.join(', ')}
          </code>. Gender: male / female / other. DOB: yyyy-mm-dd. Relation: father / mother / guardian.
        </Typography.Paragraph>

        <Upload.Dragger
          accept=".xlsx,.xls,.csv"
          multiple={false}
          showUploadList={false}
          beforeUpload={(file) => {
            void parseFile(file).then((parsed) => {
              if ('error' in parsed) {
                setParseError(parsed.error);
                setRows(null);
                setFileName('');
              } else {
                setParseError(null);
                setRows(parsed.rows);
                setFileName(file.name);
              }
            });
            return false; // prevent auto-upload
          }}
        >
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">Click or drop an XLSX / CSV here</p>
          <p className="ant-upload-hint" style={{ fontSize: 12 }}>Nothing is uploaded to your server until you press Import.</p>
        </Upload.Dragger>

        {parseError && <Alert style={{ marginTop: 10 }} type="error" showIcon message="Could not read the file" description={parseError} />}

        {rows && (
          <div style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Typography.Text>
                <b>{fileName}</b> · parsed <b>{rows.length}</b> row{rows.length === 1 ? '' : 's'} · previewing first {Math.min(rows.length, 8)}
              </Typography.Text>
              <Space size={8}>
                <Button onClick={() => { setRows(null); setFileName(''); setParseError(null); }}>Clear</Button>
                <Button type="primary" icon={<ImportOutlined />} loading={importMut.isPending} onClick={() => importMut.mutate()}>
                  Import {rows.length} students
                </Button>
              </Space>
            </div>
            <Table
              size="small"
              pagination={false}
              rowKey={(_, i) => String(i)}
              dataSource={rows.slice(0, 8)}
              columns={[
                { title: 'Adm. no.', dataIndex: 'admission_number', key: 'adm', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
                { title: 'Name', dataIndex: 'name', key: 'name' },
                { title: 'Grade', key: 'grade', render: (_, r) => `${r.grade}${r.section ? ` · ${r.section}` : ''}` },
                { title: 'Gender', dataIndex: 'gender', key: 'gender' },
                { title: 'DOB', dataIndex: 'dob', key: 'dob', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
                { title: 'Guardian', key: 'g', render: (_, r) => `${r.guardians[0]?.name} · ${r.guardians[0]?.relation}` },
                { title: 'Phone', key: 'p', render: (_, r) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{r.guardians[0]?.phone}</span> },
              ]}
            />
          </div>
        )}

        {result && (
          <div style={{ marginTop: 12 }}>
            <Alert
              type={result.errors.length === 0 ? 'success' : 'warning'}
              showIcon
              message={`Imported ${result.created} of ${result.submitted}${result.errors.length ? ` — ${result.errors.length} row${result.errors.length === 1 ? '' : 's'} skipped` : ''}`}
            />
            {result.errors.length > 0 && (
              <Table
                style={{ marginTop: 10 }}
                size="small"
                pagination={{ pageSize: 8 }}
                rowKey={(r) => `${r.row_index}-${r.admission_number}`}
                dataSource={result.errors}
                columns={[
                  { title: 'Row', dataIndex: 'row_index', key: 'row', width: 60, render: (v: number) => v + 2 },
                  { title: 'Adm. no.', dataIndex: 'admission_number', key: 'adm', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
                  { title: 'Reason', dataIndex: 'reason', key: 'reason', render: (v: string) => <Tag color={REASON_TONE[v] ?? 'default'}>{REASON_LABEL[v] ?? v}</Tag> },
                  { title: 'Detail', dataIndex: 'detail', key: 'detail', ellipsis: true },
                ]}
              />
            )}
          </div>
        )}
      </Card>

      <Card size="small" title={`Latest students (${Math.min(total, 8)} of ${total})`}>
        {total === 0 ? (
          <Empty description={<span>No students yet. <a href={`/i/${institution}/admin/students`}>Add the first one</a> or use bulk import above.</span>} style={{ padding: 20 }} />
        ) : (
          <Table
            size="small"
            pagination={false}
            rowKey="id"
            dataSource={list.data?.rows.slice(0, 8) ?? []}
            columns={[
              { title: 'Name', dataIndex: 'name', key: 'name' },
              { title: 'Adm. no.', dataIndex: 'admission_number', key: 'admission_number', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Grade', key: 'grade', render: (_, r) => `${r.grade}${r.section ? ` · ${r.section}` : ''}` },
              { title: 'Primary guardian', key: 'g', render: (_, r) => r.primary_guardian ? `${r.primary_guardian.name} · ${r.primary_guardian.relation}` : <Typography.Text type="secondary">—</Typography.Text> },
              { title: 'Status', dataIndex: 'status', key: 'status', render: (v: string) => <Tag>{v}</Tag> },
            ]}
          />
        )}
      </Card>

      {!canMarkDone && (
        <Alert type="warning" showIcon style={{ marginTop: 12 }} message="Add at least one student before marking this step done." />
      )}

      <StepFooter
        institution={institution}
        step="students"
        summary={`${total} student${total === 1 ? '' : 's'} on file`}
        disabled={!canMarkDone}
        disabledReason="Add at least one student first"
        nextHref={`/i/${institution}/setup/calendar`}
        nextLabel="Next: calendar →"
      />
    </div>
  );
}

const REASON_LABEL: Record<string, string> = {
  duplicate_admission_number: 'Duplicate adm. no.',
  guardian_primary_count: 'Guardian primary count',
  db_error: 'Database error',
  missing_columns: 'Missing columns',
  invalid_row: 'Invalid row',
};
const REASON_TONE: Record<string, string> = {
  duplicate_admission_number: 'warning',
  guardian_primary_count: 'warning',
  db_error: 'error',
};

/**
 * Parse an XLSX/CSV client-side using SheetJS.
 * Normalizes column headers (lowercase, spaces → underscores) and shapes each
 * row into a CreateStudentRequest with a single primary guardian.
 */
async function parseFile(file: File): Promise<{ rows: CreateStudentRequest[] } | { error: string }> {
  try {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const first = wb.SheetNames[0];
    if (!first) return { error: 'Workbook has no sheets.' };
    const sheet = wb.Sheets[first]!;
    const raw: Record<string, unknown>[] = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false });
    if (raw.length === 0) return { error: 'Sheet has no rows.' };

    // Normalize keys.
    const first_norm = normalizeKeys(raw[0]!);
    const missing = REQUIRED_COLS.filter((c) => !(c in first_norm));
    if (missing.length > 0) {
      return { error: `Missing columns: ${missing.join(', ')}. Rename your headers to match.` };
    }

    const rows: CreateStudentRequest[] = [];
    for (let i = 0; i < raw.length; i++) {
      const rn = normalizeKeys(raw[i]!);
      const gender = String(rn['gender'] ?? '').trim().toLowerCase();
      const relation = String(rn['guardian_relation'] ?? '').trim().toLowerCase();
      if (!['male', 'female', 'other'].includes(gender)) {
        return { error: `Row ${i + 2}: gender "${gender}" is not male / female / other.` };
      }
      if (!['father', 'mother', 'guardian'].includes(relation)) {
        return { error: `Row ${i + 2}: guardian_relation "${relation}" is not father / mother / guardian.` };
      }
      const dob = String(rn['dob'] ?? '').trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
        return { error: `Row ${i + 2}: dob "${dob}" is not yyyy-mm-dd.` };
      }
      rows.push({
        admission_number: String(rn['admission_number']).trim(),
        name: String(rn['name']).trim(),
        gender: gender as 'male' | 'female' | 'other',
        dob,
        grade: String(rn['grade']).trim(),
        section: String(rn['section'] ?? '').trim(),
        guardians: [
          {
            name: String(rn['guardian_name']).trim(),
            phone: String(rn['guardian_phone']).trim(),
            relation: relation as 'father' | 'mother' | 'guardian',
            is_primary: true,
          },
        ],
      });
    }
    return { rows };
  } catch (e) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

function normalizeKeys(row: Record<string, unknown>): Record<RequiredCol | string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(row)) {
    const norm = k.trim().toLowerCase().replace(/\s+/g, '_');
    out[norm] = v;
  }
  return out;
}
